import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from '../../../services/apiServices';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { extractObjectId } from '../../../utils/utils';
import {
  FaHeadphones,
  FaPlus,
  FaSearch,
  FaEdit,
  FaMusic,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaArrowLeft,
  FaExclamationTriangle,
  FaVolumeUp,
  FaVolumeMute,
  FaPodcast
} from 'react-icons/fa';
import './Episodes.css';

const Episodes = () => {
  const params = useParams();
  const navigate = useNavigate();
  const podcastId = params?.id || '';
  const [episodes, setEpisodes] = useState([]);
  const [podcastName, setPodcastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddEpisodeModal, setShowAddEpisodeModal] = useState(false);
  const [showEditEpisodeModal, setShowEditEpisodeModal] = useState(false);
  const [showDeleteEpisodeModal, setShowDeleteEpisodeModal] = useState(false);
  const [episodeToEdit, setEpisodeToEdit] = useState({});
  const [episodeToDelete, setEpisodeToDelete] = useState("");
  const [showDeleteAudioModal, setShowDeleteAudioModal] = useState(false);
  const [showAudioEditModel, setShowAudioEditModel] = useState(false);

  const fetchEpisodes = async (page = 1, query = '') => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getEpisodes({ podcastId, page, search: query });
      setEpisodes(response.data?.episodes || []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
      if (response.data?.podcastName) {
        setPodcastName(response.data.podcastName);
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
      toast.error('Failed to fetch episodes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEpisodes(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchEpisodes(1, searchQuery);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleAddEpisode = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('name').trim();
    const description = formData.get('description').trim();
    const audioFile = formData.get('audio');
    const image = formData.get("image");

    if (!name || !description) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const episodeData = new FormData();
    episodeData.append('name', name);
    episodeData.append('description', description);
    episodeData.append('podcastId', podcastId);

    if (audioFile && audioFile.size > 0) {
      episodeData.append('audio', audioFile);
    }

    if (image && image.size > 0) {
      episodeData.append("image", image);
    }

    try {
      setLoading(true);
      await allAPiServicesCall.createEpisode(episodeData);
      setShowAddEpisodeModal(false);
      fetchEpisodes(currentPage, searchQuery);
      toast.success('Episode added successfully.');
    } catch (error) {
      console.error('Error adding episode:', error);
      toast.error('Failed to add episode.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEpisode = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('name').trim();
    const description = formData.get('description').trim();
    const image = formData.get("image");

    if (!name || !description) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const episodeData = { name, description };
    if (image) episodeData.image = image;

    try {
      setLoading(true);
      await allAPiServicesCall.editEpisode(extractObjectId(episodeToEdit._id), podcastId, episodeData);
      setShowEditEpisodeModal(false);
      fetchEpisodes(currentPage, searchQuery);
      toast.success('Episode updated successfully.');
    } catch (error) {
      console.error('Error editing episode:', error);
      toast.error('Failed to update episode.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEpisode = async () => {
    try {
      setLoading(true);
      await allAPiServicesCall.deleteEpisode(episodeToDelete);
      setShowDeleteEpisodeModal(false);
      fetchEpisodes(currentPage, searchQuery);
      toast.success('Episode deleted successfully.');
    } catch (error) {
      console.error('Error deleting episode:', error);
      toast.error('Failed to delete episode.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAudio = async () => {
    try {
      setLoading(true);
      await allAPiServicesCall.deleteEpisodeAudio(extractObjectId(episodeToEdit?._id));
      setShowDeleteAudioModal(false);
      setShowAudioEditModel(false);
      fetchEpisodes(currentPage, searchQuery);
      toast.success('Audio deleted successfully.');
    } catch (error) {
      console.error('Error deleting audio:', error);
      toast.error('Failed to delete audio.');
    } finally {
      setLoading(false);
    }
  };

  const handleAudioSave = async (e) => {
    e.preventDefault();
    const audioFile = e.target.newAudioFile.files[0];
    const formData = new FormData();
    formData.append('audio', audioFile);

    try {
      setLoading(true);
      await allAPiServicesCall.updateEpisodeAudio(extractObjectId(episodeToEdit._id), formData, podcastId);
      toast.success('Audio updated successfully.');
      setShowAudioEditModel(false);
      fetchEpisodes(currentPage, searchQuery);
    } catch (error) {
      console.error('Error updating audio:', error);
      toast.error('Failed to update audio.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonLayout>
      <div className="episodes-page">
        {/* Back Button */}
        <button
          className="episodes-back-btn"
          onClick={() => navigate('/podcast-section')}
        >
          <FaArrowLeft /> Back to Podcasts
        </button>

        {/* Header Section */}
        <div className="episodes-header">
          <div className="episodes-header-top">
            <div className="episodes-title-wrapper">
              <div className="episodes-title-icon">
                <FaHeadphones />
              </div>
              <div className="episodes-title-text">
                <h1 className="episodes-title">Podcast Episodes</h1>
                <p className="episodes-subtitle">
                  {podcastName ? (
                    <>Managing episodes for <span className="episodes-podcast-name">{podcastName}</span></>
                  ) : (
                    'Manage episodes for this podcast'
                  )}
                </p>
              </div>
            </div>
            <div className="episodes-stats">
              <div className="episodes-stat-badge">
                <FaPodcast />
                <span>Episodes: <strong>{episodes?.length || 0}</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="episodes-action-bar">
          <div className="episodes-search-wrapper">
            <FaSearch className="episodes-search-icon" />
            <input
              type="text"
              className="episodes-search-input"
              placeholder="Search episodes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            className="episodes-btn-add"
            onClick={() => setShowAddEpisodeModal(true)}
          >
            <FaPlus /> Add Episode
          </button>
        </div>

        {/* Main Card */}
        <div className="episodes-card">
          {loading ? (
            <div className="episodes-loading">
              <div className="episodes-loading-spinner"></div>
              <span className="episodes-loading-text">Loading episodes...</span>
            </div>
          ) : episodes?.length === 0 ? (
            <div className="episodes-empty">
              <div className="episodes-empty-icon">
                <FaHeadphones />
              </div>
              <span className="episodes-empty-text">No episodes found</span>
              <span className="episodes-empty-subtitle">
                Start by adding your first episode using the button above.
              </span>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="episodes-table-container">
                <table className="episodes-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th style={{ width: '25%' }}>Title</th>
                      <th style={{ width: '40%' }}>Description</th>
                      <th style={{ width: '12%' }}>Audio</th>
                      <th style={{ width: 140 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {episodes.map((episode, index) => (
                      <tr key={episode._id}>
                        <td>
                          <span className="episodes-id">
                            {(currentPage - 1) * 10 + index + 1}
                          </span>
                        </td>
                        <td>
                          <span className="episodes-name">{episode.name}</span>
                        </td>
                        <td>
                          <span className="episodes-description">
                            {episode.description?.length > 120
                              ? episode.description.substring(0, 120) + "..."
                              : episode.description || "—"}
                          </span>
                        </td>
                        <td>
                          {episode.audioUrl ? (
                            <span className="episodes-audio-badge">
                              <FaVolumeUp size={10} /> Audio
                            </span>
                          ) : (
                            <span className="episodes-no-audio-badge">
                              <FaVolumeMute size={10} /> No Audio
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="episodes-actions">
                            <button
                              className="episodes-btn episodes-btn-edit"
                              title="Edit Episode"
                              onClick={() => {
                                setEpisodeToEdit(episode);
                                setShowEditEpisodeModal(true);
                              }}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="episodes-btn episodes-btn-audio"
                              title="Edit Audio"
                              onClick={() => {
                                setShowAudioEditModel(true);
                                setEpisodeToEdit(episode);
                              }}
                            >
                              <FaMusic />
                            </button>
                            <button
                              className="episodes-btn episodes-btn-delete"
                              title="Delete Episode"
                              onClick={() => {
                                setEpisodeToDelete(extractObjectId(episode._id));
                                setShowDeleteEpisodeModal(true);
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="episodes-mobile-view">
                {episodes.map((episode, index) => (
                  <div key={episode._id} className="episodes-mobile-card">
                    <div className="episodes-mobile-card-header">
                      <div className="episodes-mobile-card-icon">
                        <FaHeadphones />
                      </div>
                      <div className="episodes-mobile-card-info">
                        <h3 className="episodes-mobile-card-name">{episode.name}</h3>
                        <span className="episodes-mobile-card-audio">
                          {episode.audioUrl ? (
                            <span className="episodes-audio-badge">
                              <FaVolumeUp size={10} /> Audio Available
                            </span>
                          ) : (
                            <span className="episodes-no-audio-badge">
                              <FaVolumeMute size={10} /> No Audio
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    <p className="episodes-mobile-card-description">
                      {episode.description?.length > 100
                        ? episode.description.substring(0, 100) + "..."
                        : episode.description || "No description available."}
                    </p>
                    <div className="episodes-mobile-card-actions">
                      <button
                        className="episodes-btn episodes-btn-edit"
                        onClick={() => {
                          setEpisodeToEdit(episode);
                          setShowEditEpisodeModal(true);
                        }}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="episodes-btn episodes-btn-audio"
                        onClick={() => {
                          setShowAudioEditModel(true);
                          setEpisodeToEdit(episode);
                        }}
                      >
                        <FaMusic />
                      </button>
                      <button
                        className="episodes-btn episodes-btn-delete"
                        onClick={() => {
                          setEpisodeToDelete(extractObjectId(episode._id));
                          setShowDeleteEpisodeModal(true);
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="episodes-pagination">
                  <button
                    className="episodes-pagination-btn"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft /> Previous
                  </button>
                  <div className="episodes-pagination-info">
                    <span className="episodes-pagination-current">{currentPage}</span>
                    <span className="episodes-pagination-separator">/</span>
                    <span className="episodes-pagination-total">{totalPages}</span>
                  </div>
                  <button
                    className="episodes-pagination-btn"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add Episode Modal */}
        <Modal
          show={showAddEpisodeModal}
          onHide={() => setShowAddEpisodeModal(false)}
          centered
          className="episodes-modal"
        >
          <Modal.Header closeButton className="episodes-modal-header">
            <Modal.Title className="episodes-modal-title">
              <FaPlus /> Add New Episode
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="episodes-modal-body">
            <Form onSubmit={handleAddEpisode}>
              <Form.Group className="mb-3">
                <Form.Label>Episode Name *</Form.Label>
                <Form.Control
                  name="name"
                  type="text"
                  placeholder="Enter episode name"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  name="description"
                  as="textarea"
                  rows={3}
                  placeholder="Enter episode description"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Upload Audio File</Form.Label>
                <Form.Control
                  type="file"
                  name="audio"
                  accept="audio/*"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Episode Image</Form.Label>
                <Form.Control
                  type="file"
                  name="image"
                  accept="image/*"
                />
              </Form.Group>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <Button
                  className="episodes-modal-btn episodes-modal-btn-secondary"
                  onClick={() => setShowAddEpisodeModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="episodes-modal-btn episodes-modal-btn-primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Episode"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Edit Episode Modal */}
        <Modal
          show={showEditEpisodeModal}
          onHide={() => setShowEditEpisodeModal(false)}
          centered
          className="episodes-modal"
        >
          <Modal.Header closeButton className="episodes-modal-header">
            <Modal.Title className="episodes-modal-title">
              <FaEdit /> Edit Episode
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="episodes-modal-body">
            <Form onSubmit={handleEditEpisode}>
              <Form.Group className="mb-3">
                <Form.Label>Episode Name *</Form.Label>
                <Form.Control
                  name="name"
                  type="text"
                  defaultValue={episodeToEdit.name}
                  required
                />
              </Form.Group>

              {episodeToEdit.image?.url && (
                <div className="mb-3">
                  <Form.Label>Current Image</Form.Label>
                  <div>
                    <img
                      src={episodeToEdit.image?.url}
                      alt="Episode Preview"
                      className="episodes-edit-image-preview"
                    />
                  </div>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Episode Image</Form.Label>
                <Form.Control
                  type="file"
                  name="image"
                  accept="image/*"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  name="description"
                  as="textarea"
                  rows={3}
                  defaultValue={episodeToEdit.description}
                  required
                />
              </Form.Group>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <Button
                  className="episodes-modal-btn episodes-modal-btn-secondary"
                  onClick={() => setShowEditEpisodeModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="episodes-modal-btn episodes-modal-btn-primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Delete Episode Modal */}
        <Modal
          show={showDeleteEpisodeModal}
          onHide={() => setShowDeleteEpisodeModal(false)}
          centered
          className="episodes-modal"
        >
          <Modal.Header closeButton className="episodes-modal-header">
            <Modal.Title className="episodes-modal-title">
              <FaExclamationTriangle /> Confirm Delete
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="episodes-modal-body">
            <div className="episodes-delete-warning">
              <div className="episodes-delete-warning-icon">
                <FaExclamationTriangle />
              </div>
              <div className="episodes-delete-warning-text">
                Are you sure you want to delete this episode? This action cannot be undone.
                All associated audio and data will be permanently removed.
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="episodes-modal-footer">
            <Button
              className="episodes-modal-btn episodes-modal-btn-secondary"
              onClick={() => setShowDeleteEpisodeModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="episodes-modal-btn episodes-modal-btn-danger"
              onClick={handleDeleteEpisode}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete Episode"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Audio Modal */}
        <Modal
          show={showDeleteAudioModal}
          onHide={() => setShowDeleteAudioModal(false)}
          centered
          className="episodes-modal"
        >
          <Modal.Header closeButton className="episodes-modal-header">
            <Modal.Title className="episodes-modal-title">
              <FaExclamationTriangle /> Confirm Delete Audio
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="episodes-modal-body">
            <div className="episodes-delete-warning">
              <div className="episodes-delete-warning-icon">
                <FaExclamationTriangle />
              </div>
              <div className="episodes-delete-warning-text">
                Are you sure you want to delete the audio from this episode?
                This action cannot be undone.
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="episodes-modal-footer">
            <Button
              className="episodes-modal-btn episodes-modal-btn-secondary"
              onClick={() => setShowDeleteAudioModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="episodes-modal-btn episodes-modal-btn-danger"
              onClick={handleDeleteAudio}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete Audio"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Audio Modal */}
        <Modal
          show={showAudioEditModel}
          onHide={() => setShowAudioEditModel(false)}
          centered
          className="episodes-modal"
        >
          <Modal.Header closeButton className="episodes-modal-header">
            <Modal.Title className="episodes-modal-title">
              <FaMusic /> Edit Episode Audio
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleAudioSave}>
            <Modal.Body className="episodes-modal-body">
              {episodeToEdit.audioUrl && (
                <div className="episodes-audio-section">
                  <div className="episodes-audio-label">
                    <FaVolumeUp /> Current Audio
                  </div>
                  <audio
                    controls
                    src={episodeToEdit.audioUrl}
                    className="episodes-audio-player"
                  >
                    Your browser does not support the audio element.
                  </audio>
                  <Button
                    className="episodes-modal-btn episodes-modal-btn-danger"
                    disabled={loading}
                    type="button"
                    onClick={() => setShowDeleteAudioModal(true)}
                    style={{ width: '100%' }}
                  >
                    <FaTrash style={{ marginRight: '0.5rem' }} />
                    {loading ? "Deleting Audio..." : "Delete Existing Audio"}
                  </Button>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>
                  {episodeToEdit.audioUrl ? "Upload New Audio" : "Upload Audio"}
                </Form.Label>
                <Form.Control
                  type="file"
                  name="newAudioFile"
                  accept="audio/*"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="episodes-modal-footer">
              <Button
                className="episodes-modal-btn episodes-modal-btn-secondary"
                onClick={() => setShowAudioEditModel(false)}
              >
                Cancel
              </Button>
              <Button
                className="episodes-modal-btn episodes-modal-btn-primary"
                disabled={loading}
                type="submit"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </CommonLayout>
  );
};

export default Episodes;
